'use client';
import type { Team } from '@baseball-near-you/prisma';
import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormInput,
  FormPhoneNumberInput,
  FormTextarea,
  cn,
} from '@baseball-near-you/ui';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import dayjs from 'dayjs';
import { CheckCircle, ChevronLeft, ChevronRightCircle } from 'lucide-react';
import { z } from 'zod';
import { useClaimTeamMutation } from '.';
import { Step } from './TeamSelectionModal';

export function TeamBackgroundStep({
  selectedTeam,
  setIsOpen,
  setStep,
}: {
  selectedTeam: Team | null;
  setIsOpen: (isOpen: boolean) => void;
  setStep: (step: Step) => void;
}) {
  const { handleClaimTeam, claimTeamLoading } = useClaimTeamMutation();
  const { userProfile } = useUserProfile();

  return (
    <Form
      onSubmit={async (data) => {
        if (selectedTeam && userProfile) {
          await handleClaimTeam({
            createdAt: dayjs().format('MMMM DD, YYYY hh:mm:ss A'),
            teamName: selectedTeam.name,
            userId: userProfile.id,
            firstName: data.firstName || userProfile.firstName!,
            lastName: data.lastName || userProfile.lastName!,
            teamId: `${selectedTeam.id}`,
            email: data.email || userProfile.email,
            phoneNumber: data.phoneNumber || userProfile.phoneNumber,
            background: data.background,
          });
        }
        // if (!userProfile?.completedOnboarding) {
        //   setStep(Step.Onboarding);
        //   return;
        // }
        setStep(Step.TeamSelection);
        setIsOpen(false);
      }}
      schema={z.object({
        background: z
          .string()
          .min(1, 'Required.')
          .max(2000, 'Must be less than 2000 characters'),
        firstName: z.string().min(1, 'Required.'),
        lastName: z.string().min(1, 'Required.'),
        email: z.string().email(),
        phoneNumber: z.string().min(1, 'Required.'),
      })}
      initialValues={{
        background: '',
        firstName: userProfile?.firstName ?? '',
        lastName: userProfile?.lastName ?? '',
        email: userProfile?.email ?? '',
        phoneNumber: userProfile?.phoneNumber ?? '',
      }}
    >
      <Button
        variant="ghost"
        className="absolute top-4 left-4 h-min rounded-sm p-0! opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        onClick={() => setStep(Step.TeamSelection)}
        type="button"
      >
        <ChevronLeft size={20} />
      </Button>
      <DialogHeader>
        <DialogTitle className="mt-8 mb-2">
          Please describe your relation with {selectedTeam?.name ?? 'this team'}
        </DialogTitle>
      </DialogHeader>
      <div className="flex flex-col space-y-5">
        <p className="text-left">
          We want to ensure that only the team's owner or a representative of
          the team can claim the team. Please describe your relation with this
          team.
        </p>
        <div className="flex space-x-5">
          <FormInput name="firstName" label="First Name" />
          <FormInput name="lastName" label="Last Name" />
        </div>
        <FormInput name="email" label="Email" />
        <FormPhoneNumberInput name="phoneNumber" label="Phone Number" />
        <FormTextarea
          maxLength={2000}
          name="background"
          label="Your background with the team"
        />
      </div>
      <DialogFooter className="mt-8">
        <div className="flex w-full flex-col border-t">
          {!!selectedTeam && !selectedTeam.slug && (
            <p className="mt-2 self-center truncate rounded-md border bg-gray-50 px-4 py-1 font-semibold text-lg">
              Selected team - {selectedTeam.name}
            </p>
          )}
          <Button
            className={cn(
              'mt-4 w-min self-end whitespace-nowrap bg-green-600',
              userProfile?.completedOnboarding ? 'bg-green-600' : 'bg-black'
            )}
            type="submit"
            loading={claimTeamLoading}
          >
            {userProfile?.completedOnboarding ? (
              <>
                <CheckCircle size={14} className="mr-2" />
                Submit claim
              </>
            ) : (
              <>
                Continue <ChevronRightCircle size={18} className="ml-2" />
              </>
            )}
          </Button>
        </div>
      </DialogFooter>
    </Form>
  );
}
